import { Button } from "@/components/core/buttons/button";
import { IconActivity, IconBriefcase, IconChecklist, IconDiscountCheck, IconFileSearch, IconFileSymlink } from "@tabler/icons-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import { cloneElement, isValidElement, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useCaseRecordStatusMutation } from "../../case-record/api/queries/case-records-query";

type Step = 1 | 2 | 3 | 4;

type CaseFilingStepProps = {
	step: Step;
	children: ReactNode;
	className?: string;
	title?: string;
	subtitle?: string;
};

type CaseFilingProps = {
	children: ReactNode;
	className?: string;
	onNext?: (step: Step) => void;
	onValidateStep?: (step: Step) => Promise<boolean>;
	caseRecordId?: string;
};

function isCaseFilingStepElement(element: ReactNode): element is ReactElement<CaseFilingStepProps> {
	return isValidElement(element) && typeof element.props === "object" && element.props !== null && "step" in element.props;
}

export default function CaseFiling({ children, className, onNext, onValidateStep, caseRecordId }: CaseFilingProps) {
	const [step, setStep] = useState<Step>(1);
	const router = useRouter();

	const navigate = useNavigate();
	const caseRecordStatusMutation = useCaseRecordStatusMutation();

	const steps = Array.isArray(children) ? children : [children];

	const handleNext = async () => {
		// Validate current step before moving to next
		if (onValidateStep) {
			const isValid = await onValidateStep(step);
			if (!isValid) {
				// Validation failed, don't proceed
				return;
			}
		}

		if (step < 4) {
			const nextStep = (step + 1) as Step;
			setStep(nextStep);
			onNext?.(nextStep);
		}
	};

	const handleBack = () => {
		if (step > 1) {
			const prevStep = (step - 1) as Step;
			setStep(prevStep);
			onNext?.(prevStep);
		}
	};

	const handleSubmit = async () => {
		try {
			if (caseRecordId) {
				await caseRecordStatusMutation.mutateAsync({
					id: caseRecordId,
					status: "received",
				});
				navigate({
					to: "/case-filings",
				});
			}
		} catch (error) {
			console.error("Error submitting case:", error);
		}
	};

	return (
		<div className={clsx("flex flex-col w-full gap-6", className)}>
			<div className="flex flex-row w-full">
				{[1, 2, 3, 4].map((s) => {
					const stepElement = steps.find((child) => isCaseFilingStepElement(child) && child.props.step === s);
					const title = stepElement?.props.title ?? `Step ${s}`;
					const subtitle = stepElement?.props.subtitle ?? "";

					return (
						<div
							key={s}
							className={clsx(
								"flex flex-row items-center justify-center w-full border-b-2 pb-2 gap-2 text-sm",
								step === s ? "border-primary-600 text-primary-600" : "border-gray-300 text-gray-500",
							)}
						>
							<div
								className={clsx(
									"inline-flex justify-center items-center w-[44px] h-[44px] shrink-0 rounded-full",
									step === s ? "text-primary-600 bg-primary-100" : "text-gray-400 bg-slate-100",
								)}
							>
								{s === 1 && <IconBriefcase size={24} stroke={2} />}
								{s === 2 && <IconFileSymlink size={24} stroke={2} />}
								{s === 3 && <IconFileSearch size={24} stroke={2} />}
								{s === 4 && <IconChecklist size={24} stroke={2} />}
							</div>
							<div className="flex flex-col w-full">
								<span className="uppercase text-sm font-bold opacity-70">{title}</span>
								<span className="text-base font-semibold">{subtitle}</span>
							</div>
						</div>
					);
				})}
			</div>

			<div className="flex flex-col w-full border border-slate-200 rounded-md p-6 gap-4">
				<div className="flex flex-row items-center justify-center w-full">
					<span className="inline-flex w-auto text-base bg-primary-100 py-1 px-2 rounded-md text-primary-600">Step {step} / 4</span>
				</div>
				<div className="flex flex-col w-full">
					{steps.map((child) => {
						if (isCaseFilingStepElement(child)) {
							return (
								<div key={child.props.step} className={child.props.step === step ? "block" : "hidden"}>
									{cloneElement(child)}
								</div>
							);
						}
						return null;
					})}
				</div>
			</div>

			<div className="flex justify-between">
				<Button size="default" type="button" onClick={handleBack} disabled={step === 1}>
					Prev
				</Button>

				{step < 4 ? (
					<Button size="default" type="button" onClick={handleNext}>
						Next
					</Button>
				) : (
					<Button size="default" type="button" onClick={handleSubmit} loading={caseRecordStatusMutation.isPending}>
						Submit
					</Button>
				)}
			</div>
		</div>
	);
}

export function CaseFilingStep({ children, className }: CaseFilingStepProps) {
	return <div className={clsx("", className)}>{children}</div>;
}
